/**
 * ComponentTable — runtime registry of component {@link ComponentToken}s.
 *
 * `ct(id, typeId)` is the factory the generated `ComponentTable.generated.ts` calls to
 * mint each token; minting also registers it for reverse lookup by numeric id or string
 * typeId. The numeric ids are dense, runtime-only, and derived deterministically from the
 * typeId-sorted set (serialization always uses the stable string `typeId`).
 */

import type { ComponentToken } from "./scripting/IEngineContext";

const _byTypeId = new Map<string, ComponentToken<unknown>>();
const _byId: Array<ComponentToken<unknown> | undefined> = [];

/**
 * Mint and register a component token. Called from the generated table; not intended
 * for hand use.
 */
export function ct<T = unknown>(id: number, typeId: string): ComponentToken<T> {
  const token: ComponentToken<T> = { id, typeId };
  _byTypeId.set(typeId, token as ComponentToken<unknown>);
  _byId[id] = token as ComponentToken<unknown>;
  return token;
}

/** Look up a registered token by its stable string typeId. */
export function tokenForTypeId(typeId: string): ComponentToken<unknown> | undefined {
  return _byTypeId.get(typeId);
}

/** Look up a registered token by its dense numeric id. */
export function tokenForId(id: number): ComponentToken<unknown> | undefined {
  return _byId[id];
}

/** All registered tokens (dense, in id order). */
export function allTokens(): readonly ComponentToken<unknown>[] {
  return _byId.filter((t): t is ComponentToken<unknown> => t !== undefined);
}
